import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowUp, ArrowDown, Plus, Send, RotateCcw, Wand2, AlertTriangle, User } from "lucide-react";
import { PageLoading } from "@/components/shared";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const STATUS_C = { DRAFT: "bg-slate-100 text-slate-500", READY: "bg-emerald-50 text-emerald-600", IN_PROGRESS: "bg-blue-50 text-blue-600", COMPLETED: "bg-slate-100 text-slate-500", CANCELLED: "bg-red-50 text-red-600" };

export default function CareTourDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const nav = useNavigate();

  const load = useCallback(() => api.get(`/care/tours/${id}`).then((r) => setD(r.data)).catch(() => setD(false)), [id]);
  useEffect(() => { load(); }, [load]);
  if (d === false) return <div className="py-20 text-center text-slate-400">Tour nicht gefunden.</div>;
  if (!d) return <PageLoading />;
  const tr = d.tour;
  const editable = tr.status === "DRAFT" || tr.status === "PLANNED";

  const move = async (idx, dir) => {
    const ids = d.visits.map((v) => v.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    await api.post(`/care/tours/${id}/reorder`, { visit_ids: ids }); load();
  };
  const optimize = async () => {
    const r = await api.post(`/care/tours/${id}/optimize`);
    if (r.data.ok) { toast.success("Reihenfolge optimiert"); load(); } else toast.error("Nicht genügend Koordinaten für Optimierung");
  };
  const publish = async () => {
    const r = await api.post(`/care/tours/${id}/publish`);
    if (r.data.ok) { toast.success("Tour veröffentlicht (Bereit)"); load(); }
    else r.data.errors.forEach((e) => toast.error(e));
  };
  const reopen = async () => { await api.post(`/care/tours/${id}/reopen`); toast.success("Tour wieder geöffnet"); load(); };

  return (
    <div data-testid="care-tour-detail">
      <button onClick={() => nav("/app/employer/pflege/tours")} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy"><ArrowLeft className="h-4 w-4" /> Alle Touren</button>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_C[tr.status] || "bg-slate-100 text-slate-500"}`} data-testid="tour-status">{tr.status_label}</span>
            <span className="font-mono text-xs text-slate-400">{tr.tour_number}</span>
          </div>
          <h1 className="mt-2 font-heading text-2xl font-extrabold text-navy">{tr.name}</h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-slate-500"><User className="h-4 w-4" />{tr.worker_name || "Nicht zugewiesen"} · {tr.date} · {tr.planned_start_time}–{tr.planned_end_time}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {editable && <Button variant="outline" className="rounded-full" onClick={optimize} data-testid="tour-optimize"><Wand2 className="mr-1 h-4 w-4" /> Optimieren</Button>}
          {editable && <Button className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700" onClick={publish} data-testid="tour-publish"><Send className="mr-1 h-4 w-4" /> Veröffentlichen</Button>}
          {tr.status === "READY" && <Button variant="outline" className="rounded-full" onClick={reopen} data-testid="tour-reopen"><RotateCcw className="mr-1 h-4 w-4" /> Wieder öffnen</Button>}
        </div>
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-xs text-slate-400">Besuche</div><div className="font-heading text-2xl font-extrabold text-navy">{d.visits.length}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-xs text-slate-400">Geplante Besuchszeit</div><div className="font-heading text-2xl font-extrabold text-navy">{d.total_visit_minutes} min</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-xs text-slate-400">Konflikte</div><div className={`font-heading text-2xl font-extrabold ${d.conflicts.length ? "text-red-600" : "text-navy"}`}>{d.conflicts.length}</div></div>
      </div>

      {d.conflicts.length > 0 && <div className="mb-4 space-y-2" data-testid="tour-conflicts">
        {d.conflicts.map((c, i) => <div key={i} className="flex items-center gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-800"><AlertTriangle className="h-4 w-4" /> {c.message}</div>)}
      </div>}

      <div className="rounded-2xl border border-slate-200 bg-white p-2" data-testid="tour-visit-list">
        {d.visits.map((v, i) => (
          <div key={v.id} className="flex items-center gap-3 border-b border-slate-100 p-3 last:border-0" data-testid={`tour-visit-${v.id}`}>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cyan/10 text-sm font-bold text-cyan-dark">{i + 1}</div>
            <button onClick={() => nav(`/app/employer/pflege/visits/${v.id}`)} className="flex-1 text-left">
              <div className="text-sm font-semibold text-navy">{v.patient_name} <span className="font-mono text-xs text-slate-400">{v.patient_number}</span></div>
              <div className="text-xs text-slate-500">{v.scheduled_start}–{v.scheduled_end} · {v.visit_type_label} · {v.worker_name || "—"} · {[v.street, v.city].filter(Boolean).join(", ")}</div>
            </button>
            {editable && <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-navy disabled:opacity-30" data-testid={`tour-up-${v.id}`}><ArrowUp className="h-4 w-4" /></button>
              <button onClick={() => move(i, 1)} disabled={i === d.visits.length - 1} className="text-slate-400 hover:text-navy disabled:opacity-30" data-testid={`tour-down-${v.id}`}><ArrowDown className="h-4 w-4" /></button>
            </div>}
          </div>
        ))}
        {d.visits.length === 0 && <div className="p-8 text-center text-sm text-slate-400">Noch keine Besuche. Fügen Sie unten Besuche hinzu.</div>}
      </div>

      <Button className="mt-4 rounded-full bg-navy text-white hover:bg-navy/90" onClick={() => nav(`/app/employer/pflege/visits/new?tour=${id}&date=${tr.date}`)} data-testid="tour-add-visit"><Plus className="mr-1 h-4 w-4" /> Besuch hinzufügen</Button>
    </div>
  );
}
